// Row selection for the data table.
//
// Pure and framework-free, because 06-qol §3 asks for two things that are easy to
// get subtly wrong and impossible to eyeball afterwards: shift-click ranges, and
// "select all matching filter" as an affordance genuinely distinct from "select all
// on this page".
//
// The second one is why this is a state machine rather than a `Set<string>`.
// "All 340 matching" cannot be a set of 340 ids when only 50 rows have been
// fetched. So `all-matching` is a *mode* carrying an exclusion set, and the count
// is arithmetic against the server's total. Deselecting one row out of 340 then
// stays "339 selected" instead of collapsing to the 49 that happen to be on screen.

export type SelectionMode = 'none' | 'some' | 'all-matching';

export interface SelectionState {
  readonly mode: SelectionMode;
  /** Explicitly selected ids. Meaningful only in `some`. */
  readonly ids: ReadonlySet<string>;
  /** Rows deselected out of an all-matching selection. Meaningful only in `all-matching`. */
  readonly excluded: ReadonlySet<string>;
  /** The row a shift-click extends from — the last row picked without shift. */
  readonly anchor: string | null;
}

export const EMPTY_SELECTION: SelectionState = {
  mode: 'none',
  ids: new Set(),
  excluded: new Set(),
  anchor: null,
};

export function isSelected(state: SelectionState, id: string): boolean {
  if (state.mode === 'all-matching') return !state.excluded.has(id);
  return state.ids.has(id);
}

/**
 * How many rows the bulk action bar should claim. `total` is the filtered total
 * reported by the server; without it an all-matching selection cannot state a
 * number, and the bar says "all matching" instead of lying.
 */
export function selectionCount(state: SelectionState, total: number | undefined): number | undefined {
  if (state.mode === 'none') return 0;
  if (state.mode === 'some') return state.ids.size;
  if (total === undefined) return undefined;
  return Math.max(0, total - state.excluded.size);
}

export function isSelectionEmpty(state: SelectionState): boolean {
  return state.mode === 'none';
}

/** Header checkbox state for the rows currently on screen. */
export function pageCheckboxState(
  state: SelectionState,
  pageIds: readonly string[],
): 'checked' | 'indeterminate' | 'unchecked' {
  if (pageIds.length === 0) return 'unchecked';
  let selected = 0;
  for (const id of pageIds) if (isSelected(state, id)) selected += 1;
  if (selected === 0) return 'unchecked';
  return selected === pageIds.length ? 'checked' : 'indeterminate';
}

function normalise(next: SelectionState): SelectionState {
  if (next.mode === 'some' && next.ids.size === 0) {
    return { mode: 'none', ids: new Set(), excluded: new Set(), anchor: next.anchor };
  }
  return next;
}

/**
 * Ctrl/Cmd-click, a checkbox click, or Space on the focused row. Always flips one
 * row and always moves the anchor, so a following shift-click extends from here.
 */
export function toggleRow(state: SelectionState, id: string): SelectionState {
  if (state.mode === 'all-matching') {
    const excluded = new Set(state.excluded);
    if (excluded.has(id)) excluded.delete(id);
    else excluded.add(id);
    return { ...state, excluded, anchor: id };
  }
  const ids = new Set(state.ids);
  if (ids.has(id)) ids.delete(id);
  else ids.add(id);
  return normalise({ mode: 'some', ids, excluded: new Set(), anchor: id });
}

/**
 * Shift-click. Selects every row between the anchor and `id` inclusive — it never
 * deselects, which is how every table the user has met behaves (select row 3,
 * shift-click row 9, rows 3 to 9 are selected). The anchor deliberately stays put,
 * so a second shift-click re-extends from the same origin rather than chaining.
 *
 * With no anchor, or with either end scrolled out of the loaded page, this falls
 * back to selecting the single clicked row rather than guessing at a range.
 */
export function selectRange(
  state: SelectionState,
  id: string,
  pageIds: readonly string[],
): SelectionState {
  const anchor = state.anchor;
  if (anchor === null) return toggleRow(state, id);

  const from = pageIds.indexOf(anchor);
  const to = pageIds.indexOf(id);
  if (from < 0 || to < 0) return toggleRow(state, id);

  const start = Math.min(from, to);
  const end = Math.max(from, to);

  if (state.mode === 'all-matching') {
    const excluded = new Set(state.excluded);
    for (let i = start; i <= end; i += 1) excluded.delete(pageIds[i]);
    return { ...state, excluded, anchor };
  }

  const ids = new Set(state.ids);
  for (let i = start; i <= end; i += 1) ids.add(pageIds[i]);
  return { mode: 'some', ids, excluded: new Set(), anchor };
}

/** The header checkbox: every row on this page, and only this page. */
export function setPageSelected(
  state: SelectionState,
  pageIds: readonly string[],
  selected: boolean,
): SelectionState {
  if (state.mode === 'all-matching') {
    const excluded = new Set(state.excluded);
    for (const id of pageIds) {
      if (selected) excluded.delete(id);
      else excluded.add(id);
    }
    return { ...state, excluded, anchor: null };
  }
  const ids = new Set(state.ids);
  for (const id of pageIds) {
    if (selected) ids.add(id);
    else ids.delete(id);
  }
  return normalise({ mode: 'some', ids, excluded: new Set(), anchor: null });
}

/**
 * The distinct affordance: "All 340 matching", offered only once the page is fully
 * selected, because before that it would be a different action masquerading as the
 * same one.
 */
export function selectAllMatching(state: SelectionState): SelectionState {
  return { mode: 'all-matching', ids: new Set(), excluded: new Set(), anchor: state.anchor };
}

export function clearSelection(): SelectionState {
  return EMPTY_SELECTION;
}

/**
 * Whether to offer "select all matching". Shown when the visible page is entirely
 * selected and the server says there is more behind the filter — otherwise the two
 * actions would mean the same thing and the distinction 06-qol §3 asks for is lost.
 */
export function canOfferSelectAllMatching(
  state: SelectionState,
  pageIds: readonly string[],
  total: number | undefined,
): boolean {
  if (state.mode !== 'some' || pageIds.length === 0) return false;
  if (total === undefined || total <= pageIds.length) return false;
  return pageCheckboxState(state, pageIds) === 'checked';
}

/**
 * What the bulk action bar prints. Kept here so the wording cannot drift between
 * the table, the toolbar and any future export path.
 */
export function describeSelection(state: SelectionState, total: number | undefined): string {
  const count = selectionCount(state, total);
  if (count === undefined) return 'All matching rows selected';
  if (count === 0) return 'Nothing selected';
  const noun = count === 1 ? 'row' : 'rows';
  if (state.mode === 'all-matching') return `All ${count} matching ${noun} selected`;
  return `${count} ${noun} selected`;
}

/**
 * Resolves a selection into ids the caller can act on. An all-matching selection
 * has no id list on the client by design — the caller sends the filter and the
 * exclusions to the server instead, which is the only way the count stays honest.
 */
export type ResolvedSelection =
  | { readonly kind: 'ids'; readonly ids: readonly string[] }
  | { readonly kind: 'all-matching'; readonly excluded: readonly string[] };

export function resolveSelection(state: SelectionState): ResolvedSelection {
  if (state.mode === 'all-matching') {
    return { kind: 'all-matching', excluded: [...state.excluded] };
  }
  return { kind: 'ids', ids: [...state.ids] };
}
